import { useState } from 'react';
import { Link } from 'react-router-dom';
import { FiHeart, FiShoppingCart, FiStar, FiEye } from 'react-icons/fi';
import { useCart } from '../../context/CartContext';
import { useAuth } from '../../context/AuthContext';
import { toggleWishlist } from '../../utils/api';
import toast from 'react-hot-toast';
import styles from './ProductCard.module.css';

export default function ProductCard({ product, wishlistIds = [], onWishlistChange }) {
  const { addItem } = useCart();
  const { user }   = useAuth();
  const [wishlisted, setWishlisted] = useState(wishlistIds.includes(product._id));
  const [adding, setAdding]         = useState(false);

  const discount = product.discountPrice
    ? Math.round(((product.price - product.discountPrice) / product.price) * 100) : 0;

  const handleWishlist = async (e) => {
    e.preventDefault(); e.stopPropagation();
    if (!user) { toast.error('Please login to save to wishlist'); return; }
    try {
      await toggleWishlist(product._id);
      setWishlisted(w => !w);
      toast.success(wishlisted ? 'Removed from wishlist' : '❤️ Added to wishlist');
      onWishlistChange?.();
    } catch { toast.error('Something went wrong'); }
  };

  const handleAddToCart = (e) => {
    e.preventDefault(); e.stopPropagation();
    if (product.stock === 0) return;
    setAdding(true);
    addItem(product, 1);
    toast.success(`🛒 Added to cart!`);
    setTimeout(() => setAdding(false), 800);
  };

  return (
    <article className={styles.card} itemScope itemType="https://schema.org/Product">
      <Link to={`/products/${product.slug || product._id}`} className={styles.cardLink} tabIndex={-1} aria-hidden="true">
        <div className={styles.imgWrap}>
          <img
            src={product.thumbnail || 'https://via.placeholder.com/300x300?text=No+Image'}
            alt={product.name}
            className={styles.img}
            loading="lazy"
            itemProp="image"
          />
          {product.stock === 0 && <div className={styles.outOfStock}>Out of Stock</div>}
          {discount >= 5 && product.stock > 0 && (
            <span className={styles.discTag}>-{discount}%</span>
          )}
          {product.isFeatured && product.stock > 0 && !discount && (
            <span className={styles.featuredTag}>⭐ Featured</span>
          )}
          <div className={styles.overlay} aria-hidden="true">
            <button
              className={`${styles.overlayBtn} ${wishlisted ? styles.wishlisted : ''}`}
              onClick={handleWishlist}
              title={wishlisted ? 'Remove from wishlist' : 'Add to wishlist'}
            >
              <FiHeart size={14} fill={wishlisted ? '#fff' : 'none'} />
            </button>
            <Link
              to={`/products/${product.slug || product._id}`}
              className={styles.overlayBtn}
              title="View product"
              onClick={e => e.stopPropagation()}
            >
              <FiEye size={14} />
            </Link>
          </div>
        </div>
      </Link>

      <div className={styles.info}>
        {product.brand && <p className={styles.brand} itemProp="brand">{product.brand}</p>}
        <Link to={`/products/${product.slug || product._id}`} className={styles.nameLink}>
          <h3 className={styles.name} itemProp="name">{product.name}</h3>
        </Link>

        {product.numReviews > 0 && (
          <div className={styles.rating}>
            <div className={styles.stars} aria-hidden="true">
              {[1,2,3,4,5].map(s => (
                <FiStar key={s} size={11}
                  fill={s <= Math.round(product.rating) ? '#f59e0b' : 'none'}
                  color={s <= Math.round(product.rating) ? '#f59e0b' : '#d1d5db'}
                />
              ))}
            </div>
            <span className={styles.ratingNum}>{product.rating?.toFixed(1)}</span>
            <span className={styles.reviewCount}>({product.numReviews})</span>
          </div>
        )}

        <div className={styles.priceRow}>
          {product.discountPrice ? (
            <>
              <span className={styles.curPrice}>৳{product.discountPrice.toLocaleString()}</span>
              <span className={styles.oldPrice}>৳{product.price.toLocaleString()}</span>
            </>
          ) : (
            <span className={styles.curPrice}>৳{product.price.toLocaleString()}</span>
          )}
          {product.stock > 0 && product.stock <= 5 && (
            <span className={styles.lowStock}>Only {product.stock} left!</span>
          )}
        </div>

        <button
          className={`${styles.cartBtn} ${adding ? styles.cartAdded : ''} ${product.stock === 0 ? styles.cartDisabled : ''}`}
          onClick={handleAddToCart}
          disabled={product.stock === 0 || adding}
          aria-label={`Add ${product.name} to cart`}
        >
          {adding ? (
            <><div className="spinner spinner-sm" style={{ width:14, height:14, borderWidth:2 }} /> Added!</>
          ) : product.stock === 0 ? 'Out of Stock' : (
            <><FiShoppingCart size={13} /> Add to Cart</>
          )}
        </button>
      </div>
    </article>
  );
}
